
import React, { useEffect, useState } from 'react';
import { SidebarMenuButton, SidebarMenuButtonProps, useSidebar } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';

interface SidebarMenuButtonWithAutoHideProps extends SidebarMenuButtonProps {
  onClick?: () => void;
  unreadCount?: number;
  showBadge?: boolean;
  countRefreshTrigger?: number; // Add a prop to trigger re-rendering when count changes
}

export function SidebarMenuButtonWithAutoHide({ 
  onClick, 
  children,
  unreadCount = 0,
  showBadge = false,
  countRefreshTrigger = 0, // Default to 0
  ...props 
}: SidebarMenuButtonWithAutoHideProps) {
  const { expanded, toggleExpanded } = useSidebar();
  const isMobile = useIsMobile();
  const [displayCount, setDisplayCount] = useState(unreadCount);

  // Enhanced update display count with better logging and synchronization
  useEffect(() => {
    console.log(`SidebarMenuButton: Updating count from ${displayCount} to ${unreadCount} (trigger: ${countRefreshTrigger})`);
    setDisplayCount(unreadCount);
  }, [unreadCount, countRefreshTrigger]);

  // Additional effect to handle edge cases where the count might be stuck
  useEffect(() => {
    if (countRefreshTrigger > 0) {
      console.log(`SidebarMenuButton: Global refresh triggered (${countRefreshTrigger}), forcing count update`);
      setDisplayCount(unreadCount);
    }
  }, [countRefreshTrigger, unreadCount]);

  const handleClick = () => {
    // Execute the original onClick first
    if (onClick) {
      onClick();
    }
    
    // Auto-hide sidebar on mobile after selection
    if (isMobile && expanded) {
      // Add a small delay to ensure the click action is processed first
      setTimeout(() => {
        toggleExpanded();
      }, 100);
    }
  };

  return (
    <SidebarMenuButton onClick={handleClick} {...props}>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center">
          {children}
        </div>
        {showBadge && displayCount > 0 && (
          <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center ml-2">
            {displayCount > 99 ? '99+' : displayCount}
          </span>
        )}
      </div>
    </SidebarMenuButton>
  );
}
